import { Alert, Stack } from '@mui/material';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getDateFormatted } from 'shared/dates';
import { CreateReportProvisionalSubmissionResponse } from 'features/provisionalSubmissions/provisionalSubmissions.types';
import { EvacZone, RegionEvacZoneStyle } from 'shared/types';
import { ReportGeoEventChangelog } from './ReportGeoEventChangelog';
import { ReportableGeoEvent } from './types';
import { getReviewSubmissionInitialFormValues } from './utils';

type ReviewSubmissionGeoEventChangesProps = {
  geoEvent: ReportableGeoEvent;
  reviewSubmission: CreateReportProvisionalSubmissionResponse;
  evacZones: EvacZone[];
  zoneStyle: RegionEvacZoneStyle;
};

export const ReviewSubmissionGeoEventChanges = ({
  geoEvent,
  reviewSubmission,
  evacZones,
  zoneStyle,
}: ReviewSubmissionGeoEventChangesProps): JSX.Element => {
  const { t } = useTranslation();

  const submittedFormValues = useMemo(
    () => getReviewSubmissionInitialFormValues(geoEvent, reviewSubmission),
    [geoEvent, reviewSubmission],
  );
  const methods = useForm({ defaultValues: submittedFormValues });

  return (
    <Stack spacing={1.5}>
      <Alert severity="info">
        {t('reviewSubmission.submittedByCreateReport', {
          firstName: reviewSubmission.createdBy.firstName,
          lastName: reviewSubmission.createdBy.lastName,
          date: getDateFormatted(reviewSubmission.dateCreated, {
            includeYear: true,
            includeTimeZone: true,
          }),
        })}
      </Alert>

      <FormProvider {...methods}>
        <ReportGeoEventChangelog
          geoEvent={geoEvent}
          evacZones={evacZones}
          zoneStyle={zoneStyle}
          title={t('reviewSubmission.geoEventChanges.title')}
          hideConfirmations
        />
      </FormProvider>
    </Stack>
  );
};
